import { useCallback, useEffect, useState } from 'react';
import {
  financialPlanningApi,
  type Budget,
  type CashFlowPlan,
  type FinancialDashboard,
  type FinancialPlan,
  type FinancialRecommendation,
  type FinancialRisk,
  type FinancialScenario,
  type ProfitabilityModel,
  type WorkingCapitalModel,
} from '../../api/financialplanning';

type Tab = 'dashboard' | 'budget' | 'revenue' | 'cashflow' | 'workingcapital' | 'profitability' | 'risks' | 'recommendations' | 'scenarios';
type Theme = 'light' | 'dark';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function FinancialPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<FinancialDashboard | null>(null);
  const [plan, setPlan] = useState<FinancialPlan | null>(null);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [cashFlow, setCashFlow] = useState<CashFlowPlan[]>([]);
  const [profitability, setProfitability] = useState<ProfitabilityModel[]>([]);
  const [workingCapital, setWorkingCapital] = useState<WorkingCapitalModel | null>(null);
  const [risks, setRisks] = useState<FinancialRisk[]>([]);
  const [recommendations, setRecommendations] = useState<FinancialRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<FinancialScenario[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, plans] = await Promise.all([financialPlanningApi.dashboard(), financialPlanningApi.listPlans()]);
      setDashboard(dash);
      const p = plans[0] ?? null;
      setPlan(p);
      if (!p) return;
      const [bud, cf, prof, wc, rsk, recs, sc] = await Promise.all([
        financialPlanningApi.listBudgets(p.id),
        financialPlanningApi.listCashFlow(p.id),
        financialPlanningApi.listProfitability(p.id),
        financialPlanningApi.getWorkingCapital(p.id),
        financialPlanningApi.listRisks(p.id),
        financialPlanningApi.listRecommendations(p.id),
        financialPlanningApi.listScenarios(p.id),
      ]);
      setBudgets(bud);
      setCashFlow(cf);
      setProfitability(prof);
      setWorkingCapital(wc);
      setRisks(rsk);
      setRecommendations(recs);
      setScenarios(sc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load financial planning');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'budget', label: 'Budget Workspace' },
    { id: 'cashflow', label: 'Cash Flow' },
    { id: 'workingcapital', label: 'Working Capital' },
    { id: 'profitability', label: 'Profitability' },
    { id: 'risks', label: 'Risk Dashboard' },
    { id: 'recommendations', label: 'AI CFO Advisor' },
    { id: 'scenarios', label: 'Scenarios' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Financial Planning</h1>
            <p className={`mt-1 text-sm ${muted}`}>FP&A, budgeting, cash flow planning, working capital optimization, and AI financial recommendations</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Refresh</button>
          </div>
        </div>
        {plan && (
          <p className={`mt-2 text-sm ${muted}`}>
            {plan.name} · Revenue {fmtMoney(plan.revenue_forecast)} · Margin {(plan.gross_margin * 100).toFixed(0)}% · Health {plan.health_score.toFixed(0)}
          </p>
        )}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Revenue Forecast', value: fmtMoney(dashboard.revenue_forecast) },
              { label: 'Revenue Growth', value: `${dashboard.revenue_growth.toFixed(1)}%` },
              { label: 'Gross Margin', value: `${(dashboard.gross_margin * 100).toFixed(0)}%` },
              { label: 'Operating Margin', value: `${(dashboard.operating_margin * 100).toFixed(0)}%` },
              { label: 'Net Cash Flow', value: fmtMoney(dashboard.net_cash_flow) },
              { label: 'Working Capital', value: fmtMoney(dashboard.working_capital) },
              { label: 'Cash Conversion Cycle', value: `${dashboard.cash_conversion_cycle.toFixed(0)} days` },
              { label: 'Budget Variance', value: `${dashboard.budget_variance.toFixed(1)}%` },
              { label: 'ROI', value: `${dashboard.roi.toFixed(0)}%` },
              { label: 'Health Score', value: dashboard.health_score.toFixed(0) },
              { label: 'Forecast Accuracy', value: `${dashboard.forecast_accuracy.toFixed(0)}%` },
              { label: 'Open Risks', value: dashboard.open_risks },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
        {tab === 'budget' && budgets.map((b) => (
          <div key={b.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{b.name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{b.budget_type} · {b.department ?? 'Enterprise'} · {b.fiscal_period}</p>
            <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
              <div className={`h-2 rounded-full ${b.utilization_pct >= 90 ? 'bg-amber-500' : 'bg-emerald-600'}`} style={{ width: `${Math.min(b.utilization_pct, 100)}%` }} />
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{fmtMoney(b.allocated)} / {fmtMoney(b.total_amount)} · {b.utilization_pct.toFixed(0)}% utilized</p>
          </div>
        ))}
        {tab === 'cashflow' && cashFlow.map((cf) => (
          <div key={cf.id} className={card}>
            <h3 className="text-lg font-semibold">Cash Flow — {cf.period}</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div><p className={`text-sm ${muted}`}>Inflow</p><p className="text-xl font-semibold text-emerald-600">{fmtMoney(cf.cash_inflow)}</p></div>
              <div><p className={`text-sm ${muted}`}>Outflow</p><p className="text-xl font-semibold text-red-600">{fmtMoney(cf.cash_outflow)}</p></div>
              <div><p className={`text-sm ${muted}`}>Net Cash Flow</p><p className="text-xl font-semibold">{fmtMoney(cf.net_cash_flow)}</p></div>
              <div><p className={`text-sm ${muted}`}>Cash Buffer</p><p className="text-xl font-semibold">{fmtMoney(cf.cash_buffer)}</p></div>
            </div>
          </div>
        ))}
        {tab === 'workingcapital' && workingCapital && (
          <div className={card}>
            <h3 className="text-lg font-semibold">Working Capital Optimization</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div><p className={`text-sm ${muted}`}>Working Capital</p><p className="text-xl font-semibold">{fmtMoney(workingCapital.working_capital)}</p></div>
              <div><p className={`text-sm ${muted}`}>CCC</p><p className="text-xl font-semibold">{workingCapital.cash_conversion_cycle.toFixed(0)} days</p></div>
              <div><p className={`text-sm ${muted}`}>DIO / DSO / DPO</p><p className="text-xl font-semibold">{workingCapital.dio}/{workingCapital.dso}/{workingCapital.dpo}</p></div>
              <div><p className={`text-sm ${muted}`}>Utilization</p><p className="text-xl font-semibold">{workingCapital.utilization_pct.toFixed(0)}%</p></div>
            </div>
            {workingCapital.optimization_note && <p className={`mt-4 text-sm ${muted}`}>{workingCapital.optimization_note}</p>}
          </div>
        )}
        {tab === 'profitability' && profitability.map((p) => (
          <div key={p.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{p.dimension_name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{p.dimension} · Score {p.profitability_score.toFixed(0)}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              <span>Revenue {fmtMoney(p.revenue)}</span>
              <span>Gross {fmtMoney(p.gross_profit)}</span>
              <span>Contrib {(p.contribution_margin * 100).toFixed(0)}%</span>
              <span>Op {(p.operating_margin * 100).toFixed(0)}%</span>
            </div>
          </div>
        ))}
        {tab === 'risks' && risks.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.risk_type.replace(/_/g, ' ')} · {r.severity}</h3>
            <p className={`mt-1 text-sm ${muted}`}>Probability {(r.probability * 100).toFixed(0)}%</p>
            <p className="mt-2 text-sm">{r.business_impact}</p>
            <p className="mt-1 text-sm">Mitigation: {r.recommended_mitigation}</p>
          </div>
        ))}
        {tab === 'recommendations' && recommendations.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.title}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{r.type.replace(/_/g, ' ')} · ROI {r.expected_roi.toFixed(0)}% · Savings {fmtMoney(r.expected_savings)}</p>
            <p className="mt-2 text-sm">{r.business_explanation}</p>
          </div>
        ))}
        {tab === 'scenarios' && scenarios.map((s) => (
          <div key={s.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{s.name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{s.scenario_type.replace(/_/g, ' ')}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              <span>Revenue {fmtMoney(s.revenue)}</span>
              <span>Margin {(s.gross_margin * 100).toFixed(0)}%</span>
              <span>Cash {fmtMoney(s.cash_flow)}</span>
              <span>ROI {s.roi.toFixed(0)}%</span>
            </div>
            {s.description && <p className="mt-2 text-sm">{s.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
